import React, { useMemo } from 'react';
import Card from '@/components/UI/Card';
import DataTable from '@/components/UI/DataTable';
import EmptyState from '@/components/UI/EmptyState';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { getDateFromUnix } from '@einhaus/meteoric-helpers';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { PortfolioGovernanceDecisionsRow } from '@einhaus/project314-types';
import { formatPositionSide, formatUnderlyingBias } from '@/utils/tradeSemantics';

interface PortfolioGovernanceDecisionsCardProps {
    portfolioId: string | undefined;
}

type GovernanceDecision = PortfolioGovernanceDecisionsRow['decision'];

const DECISION_BADGE_CLASS: Record<GovernanceDecision, string> = {
    accept: 'bg-green-900 text-green-200',
    blockNew: 'bg-red-900 text-red-200',
    replaceExisting: 'bg-amber-900 text-amber-200',
    closeOpposing: 'bg-amber-900 text-amber-200',
    manualReview: 'bg-blue-900 text-blue-200'
};

const DECISION_LABEL: Record<GovernanceDecision, string> = {
    accept: 'Accepted',
    blockNew: 'Blocked',
    replaceExisting: 'Replaced',
    closeOpposing: 'Closed opposing',
    manualReview: 'Manual review'
};

const PortfolioGovernanceDecisionsCard: React.FC<PortfolioGovernanceDecisionsCardProps> = ({ portfolioId }) => {
    const numericPortfolioId = portfolioId ? parseInt(portfolioId) : 0;

    const decisionsQuery = useAppQuery(
        {
            ...trpc.portfolioGovernance.getDecisions.queryOptions({ portfolioId: numericPortfolioId, limit: 200 }),
            enabled: numericPortfolioId > 0
        },
        { fallbackErrorMessage: 'Failed to load governance decisions' }
    );

    const decisions = decisionsQuery.data?.decisions ?? [];

    const columns = useMemo<ColumnDef<PortfolioGovernanceDecisionsRow>[]>(
        () => [
            {
                accessorKey: 'decisionTimestamp',
                header: 'When',
                cell: ({ row }) => getDateFromUnix(row.original.decisionTimestamp, 'readable1'),
                size: 160
            },
            {
                id: 'entity',
                header: 'Entity',
                accessorFn: (row) => `${row.entityType} #${row.entityId}`,
                size: 150
            },
            {
                accessorKey: 'direction',
                header: 'Position side',
                cell: ({ row }) => formatPositionSide(row.original.direction),
                size: 105
            },
            {
                accessorKey: 'underlyingBias',
                header: 'Underlying exposure',
                cell: ({ row }) =>
                    row.original.underlyingBias === null ? 'Unavailable' : formatUnderlyingBias(row.original.underlyingBias),
                size: 145
            },
            {
                accessorKey: 'decision',
                header: 'Decision',
                cell: ({ row }) => {
                    const { decision } = row.original;

                    return (
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${DECISION_BADGE_CLASS[decision]}`}>
                            {DECISION_LABEL[decision]}
                        </span>
                    );
                },
                size: 120
            },
            {
                accessorKey: 'reasonCode',
                header: 'Reason',
                size: 180
            },
            {
                accessorKey: 'candidateStrategyId',
                header: 'Strategy',
                cell: ({ row }) => `#${row.original.candidateStrategyId}`,
                size: 90
            },
            {
                accessorKey: 'candidateFamilyId',
                header: 'Family',
                cell: ({ row }) => (row.original.candidateFamilyId === null ? '-' : `#${row.original.candidateFamilyId}`),
                size: 90
            },
            {
                accessorKey: 'contentionRank',
                header: 'Rank',
                size: 70
            },
            {
                accessorKey: 'selectionMetricValue',
                header: 'Metric',
                cell: ({ row }) => row.original.selectionMetricValue ?? '—',
                size: 110
            }
        ],
        []
    );

    return (
        <Card
            className="mb-4"
            subtitle="Recent admission decisions made when strategies competed for the same entity."
            title="Governance Decision History"
        >
            {decisions.length === 0 ? (
                <EmptyState size="sm" title="No governance decisions recorded yet." />
            ) : (
                <DataTable search striped columns={columns} data={decisions} pagination={{ enabled: true, pageSize: 25 }} />
            )}
        </Card>
    );
};

export default PortfolioGovernanceDecisionsCard;
